import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAccount } from "wagmi";
import { fetchX402WalletEndpoints, type X402DiscoveredEndpoint } from "../../lib/x402Discovery";
import { fetchPublicX402ProfileByWallet, toBaseAccountId } from "../../lib/x402Profile";

/**
 * Enter an x402 wallet address to pull up the endpoints it has recently paid,
 * then pick one to review. Each distinct USDC recipient is resolved against
 * Coinbase's Bazaar registry, so this spans every x402 provider, not just Boon.
 * Matching is provider-level: a payTo is a provider treasury, so results can
 * include sibling endpoints of a provider you paid.
 */
export function X402WalletEndpoints({ onPick }: { onPick: (url: string) => void }) {
  const { address } = useAccount();
  const [wallet, setWallet] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [endpoints, setEndpoints] = useState<X402DiscoveredEndpoint[]>([]);
  const [lookupContext, setLookupContext] = useState<{
    wallet: string;
    profileId: string | null;
    profileStatus: "loading" | "available" | "unavailable";
  } | null>(null);
  const requestId = useRef(0);
  const [open, setOpen] = useState(false);

  async function lookup(target: string) {
    const value = target.trim();
    if (!/^0x[0-9a-fA-F]{40}$/.test(value)) {
      setError("Enter a valid wallet address (0x…).");
      setStatus("error");
      return;
    }
    const currentRequest = ++requestId.current;
    setStatus("loading");
    setError(null);
    setLookupContext({ wallet: value, profileId: null, profileStatus: "loading" });
    try {
      const [found, profileResult] = await Promise.all([
        fetchX402WalletEndpoints(value),
        fetchPublicX402ProfileByWallet(toBaseAccountId(value))
          .then((result) => ({ status: "available" as const, profileId: result.profile.id }))
          .catch(() => ({ status: "unavailable" as const, profileId: null })),
      ]);
      if (requestId.current !== currentRequest) return;
      setEndpoints(found);
      setLookupContext({ wallet: value, profileId: profileResult.profileId, profileStatus: profileResult.status });
      setStatus("done");
    } catch (cause) {
      if (requestId.current !== currentRequest) return;
      setError(cause instanceof Error ? cause.message : String(cause));
      setStatus("error");
    }
  }

  return (
    <div className="mt-5 border-t border-faint pt-4">
      {open ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-ink">Providers this wallet has paid</p>
            <button type="button" className="btn-mono text-xs text-muted hover:text-ink" onClick={() => setOpen(false)}>Hide −</button>
          </div>
          <p id="x402-wallet-route-help" className="mt-1 text-xs text-muted leading-relaxed max-w-3xl">
            Enter an x402 wallet to match recent Base USDC recipients against Coinbase Bazaar. This proves a payment to the provider, not which sibling endpoint was used. Pick only the exact route you recognize.
          </p>
          <form
            className="mt-3 grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]"
            onSubmit={(event) => { event.preventDefault(); void lookup(wallet); }}
          >
            <label className="block min-w-0" htmlFor="x402-wallet-route-lookup">
              <span className="sr-only">x402 wallet address</span>
              <input
                id="x402-wallet-route-lookup"
                aria-describedby="x402-wallet-route-help"
                className="w-full min-h-11 border border-faint rounded-md bg-paper px-4 py-2.5 text-sm font-mono"
                value={wallet}
                onChange={(event) => setWallet(event.target.value)}
                placeholder="0x… x402 wallet address"
                spellCheck={false}
              />
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <button type="submit" className="btn btn-primary min-h-11 w-full sm:w-auto" disabled={status === "loading"}>
                {status === "loading" ? "Looking up…" : "Show provider matches"}
              </button>
              {address && (
                <button
                  type="button"
                  className="btn btn-ghost min-h-11 w-full sm:w-auto"
                  onClick={() => { setWallet(address); void lookup(address); }}
                  disabled={status === "loading"}
                >
                  Use connected
                </button>
              )}
            </div>
          </form>

          {status === "error" && error && <p className="mt-3 text-sm text-danger break-words" role="alert">{error}</p>}
          {lookupContext && status === "done" && (
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-faint py-3 text-xs">
              <span className="btn-mono text-muted">Wallet {lookupContext.wallet.slice(0, 6)}…{lookupContext.wallet.slice(-4)}</span>
              <Link className="btn-mono text-olive-deep hover:underline" to={`/x402/reviewers/${lookupContext.wallet}`}>Reviewer activity</Link>
              {lookupContext.profileStatus === "available" && lookupContext.profileId ? (
                <Link className="btn-mono text-olive-deep hover:underline" to={`/x402/profiles/${lookupContext.profileId}`}>Public linked-wallet profile</Link>
              ) : lookupContext.profileStatus === "loading" ? (
                <span className="btn-mono text-muted">Checking public profile…</span>
              ) : (
                <span className="text-muted">No public linked-wallet profile was returned.</span>
              )}
            </div>
          )}
          {status === "done" && endpoints.length === 0 && (
            <p className="mt-3 text-sm text-muted">No recent Base USDC recipients from this wallet matched an x402 provider in Bazaar. If your agent pays from a different wallet, try that address.</p>
          )}
          {status === "done" && endpoints.length > 0 && (
            <ul className="mt-3 grid gap-2">
              {endpoints.map((endpoint) => (
                <li key={`${endpoint.routeId}:${endpoint.url}`}>
                  <button
                    type="button"
                    onClick={() => onPick(endpoint.url)}
                    className="w-full text-left border border-faint rounded-md px-4 py-3 hover:border-ink-soft transition-colors"
                  >
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="text-sm text-ink font-display truncate">{endpoint.service ?? endpoint.label}</span>
                        <span className="btn-mono text-[10px] uppercase tracking-[0.12em] text-muted border border-faint rounded px-1.5 py-0.5 shrink-0">provider match</span>
                      </span>
                      {endpoint.priceUsd && <span className="btn-mono text-xs text-muted shrink-0">${endpoint.priceUsd}</span>}
                    </span>
                    <span className="mt-0.5 block btn-mono text-xs text-olive-deep break-all">{endpoint.method} {endpoint.url}</span>
                    {endpoint.description && <span className="mt-1 block text-xs text-muted line-clamp-2">{endpoint.description}</span>}
                    <span className="mt-1 block text-xs text-olive-deep">This exact route was used? Review it →</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <button type="button" className="btn-mono text-xs text-olive-deep hover:underline" onClick={() => setOpen(true)}>
          Or match a wallet's paid providers +
        </button>
      )}
    </div>
  );
}
